import { Badge } from "@sylviq/ui-react-web/components/ui/badge";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Input } from "@sylviq/ui-react-web/components/ui/input";
import { useEffect, useRef, useState } from "react";
import {
  createBill,
  listChartAccounts,
  type ChartAccount,
} from "@/lib/api";
import { toMinorUnits } from "@/lib/money";
import { parseReceiptText } from "./receipt-ocr";
import { extractReceiptFile } from "./receipt-scan";

type ReceiptRow = {
  key: string;
  fileName: string;
  image?: string;
  vendor: string;
  date: string;
  total: string;
  reference: string;
  accountId: string;
  hints: string[];
};

function rowIssues(row: ReceiptRow, accounts: ChartAccount[]): string[] {
  const issues: string[] = [];
  if (!row.vendor.trim()) issues.push("Enter the supplier.");
  if (!/^\d{4}-\d{2}-\d{2}$/.test(row.date) || Number.isNaN(Date.parse(`${row.date}T00:00:00Z`))) {
    issues.push("Enter the receipt date.");
  }
  if (toMinorUnits(row.total, "USD") === null) issues.push("Enter the receipt total.");
  if (!accounts.some((account) => account.id === effectiveAccount(row, accounts))) {
    issues.push("Choose the expense account.");
  }
  return issues;
}

/** A row scanned before the chart arrived has no stored account yet; the
 * select and the create payload both fall back to the default expense. */
function effectiveAccount(
  row: ReceiptRow,
  accounts: ChartAccount[],
): string {
  return row.accountId || defaultAccount(accounts);
}

function defaultAccount(accounts: ChartAccount[]): string {
  return (
    accounts.find((account) => account.code === "5000")?.id ??
    accounts[0]?.id ??
    ""
  );
}

function totalInput(cents: number | null): string {
  return cents === null ? "" : (cents / 100).toFixed(2);
}

export function ReceiptIntake({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated?: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const controllerRef = useRef<AbortController | null>(null);
  const [accounts, setAccounts] = useState<ChartAccount[] | null>(null);
  const [accountError, setAccountError] = useState<string | null>(null);
  const [rows, setRows] = useState<ReceiptRow[]>([]);
  const [scanning, setScanning] = useState(false);
  const [progress, setProgress] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let active = true;
    listChartAccounts()
      .then((all) => {
        if (!active) return;
        const expenses = all.filter(
          (account) => account.active && account.account_type === "expense",
        );
        setAccounts(expenses);
        const accountId = defaultAccount(expenses);
        if (accountId) {
          setRows((current) =>
            current.map((row) =>
              row.accountId ? row : { ...row, accountId },
            ),
          );
        }
      })
      .catch((cause) => {
        if (active) {
          setAccountError(cause instanceof Error ? cause.message : "Expense accounts could not be read.");
          setAccounts([]);
        }
      });
    return () => {
      active = false;
      controllerRef.current?.abort();
    };
  }, []);

  const scan = async (files: File[]) => {
    if (files.length === 0) return;
    setError(null);
    setNotice(null);
    setScanning(true);
    const controller = new AbortController();
    controllerRef.current = controller;
    const failures: string[] = [];
    for (const file of files) {
      try {
        setProgress(`Reading ${file.name} on this device…`);
        const scanned = await extractReceiptFile(file, controller.signal, setProgress);
        const fields = parseReceiptText(scanned.text);
        const accountId = defaultAccount(accounts ?? []);
        setRows((current) => [
          ...current,
          {
            key: `${file.name}:${file.size}:${current.length}`,
            fileName: file.name,
            image: scanned.image,
            vendor: fields.vendor ?? "",
            date: fields.date ?? "",
            total: totalInput(fields.total_cents),
            reference: fields.reference ?? "",
            accountId,
            hints: fields.issues,
          },
        ]);
      } catch (cause) {
        if (controller.signal.aborted) break;
        failures.push(`${file.name}: ${cause instanceof Error ? cause.message : "could not be read"}`);
      }
    }
    setScanning(false);
    setProgress("");
    if (failures.length > 0) setError(failures.join(" "));
    if (inputRef.current) inputRef.current.value = "";
  };

  const update = (key: string, patch: Partial<ReceiptRow>) => {
    setRows((current) =>
      current.map((row) => (row.key === key ? { ...row, ...patch } : row)),
    );
  };

  const remove = (key: string) => {
    setRows((current) => current.filter((row) => row.key !== key));
  };

  const blocked =
    !accounts ||
    accounts.length === 0 ||
    rows.length === 0 ||
    submitting ||
    scanning ||
    rows.some((row) => rowIssues(row, accounts).length > 0);

  const submit = async () => {
    if (!accounts || accounts.length === 0) return;
    setSubmitting(true);
    setError(null);
    setNotice(null);
    const created: string[] = [];
    try {
      for (const row of rows) {
        const amount = toMinorUnits(row.total, "USD");
        if (amount === null) throw new Error(`${row.fileName}: enter a positive total.`);
        const due = new Date(`${row.date}T00:00:00Z`);
        if (Number.isNaN(due.getTime())) throw new Error(`${row.fileName}: enter a valid date.`);
        due.setUTCDate(due.getUTCDate() + 30);
        await createBill({
          supplier_name: row.vendor.trim(),
          issue_date: row.date,
          due_at: due.toISOString(),
          lines: [
            {
              account_id: effectiveAccount(row, accounts),
              description: row.reference.trim()
                ? `Receipt ${row.reference.trim()}`
                : row.fileName,
              amount_minor: amount,
            },
          ],
        });
        created.push(row.key);
      }
      setRows((current) => current.filter((row) => !created.includes(row.key)));
      setNotice(
        `Created ${created.length} draft ${created.length === 1 ? "bill" : "bills"}. Review and receive them from the bills list.`,
      );
      onCreated?.();
    } catch (cause) {
      if (created.length > 0) {
        setRows((current) => current.filter((row) => !created.includes(row.key)));
      }
      setError(cause instanceof Error ? cause.message : "The bills could not be created.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="grid gap-4" aria-label="Scan receipts">
      <p className="text-sm">
        Receipt PDFs and images are read on this device with English OCR.
        Check every supplier, date, total, reference, and account against the
        original before creating the draft bills.
      </p>
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,image/png,image/jpeg"
        multiple
        className="sr-only"
        aria-label="Choose receipt files"
        onChange={(event) => void scan(Array.from(event.target.files ?? []))}
      />
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="secondary"
          onClick={() => inputRef.current?.click()}
          disabled={scanning || !accounts}
        >
          {scanning ? "Reading receipts…" : "Choose receipts"}
        </Button>
        {accounts === null ? <span className="text-sm">Reading expense accounts…</span> : null}
        {accounts?.length === 0 ? (
          <span role="alert" className="text-sm text-[var(--danger)]">
            Add an expense account before scanning receipts.
          </span>
        ) : null}
      </div>
      {progress ? <p role="status">{progress}</p> : null}
      {accountError ? (
        <p role="alert" className="text-sm text-[var(--danger)]">
          {accountError}
        </p>
      ) : null}
      {error ? (
        <p role="alert" className="text-sm text-[var(--danger)]">
          {error}
        </p>
      ) : null}
      {notice ? (
        <p role="status" className="text-sm">
          {notice}
        </p>
      ) : null}
      {rows.length === 0 && !scanning ? (
        <p className="text-sm text-[var(--muted-foreground)]">
          Choose one or more receipts. Fields that OCR cannot read stay blank
          for you to complete.
        </p>
      ) : null}
      {rows.map((row) => {
        const issues = accounts ? rowIssues(row, accounts) : [];
        return (
          <article
            key={row.key}
            data-testid="receipt-row"
            className="grid gap-3 rounded-xl border border-[var(--border)] p-3"
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Badge>{row.fileName}</Badge>
                {issues.length === 0 ? (
                  <Badge tone="success">Ready</Badge>
                ) : (
                  <Badge tone="warning">Needs review</Badge>
                )}
              </div>
              <Button type="button" variant="ghost" size="sm" onClick={() => remove(row.key)}>
                Remove
              </Button>
            </div>
            {row.image ? (
              <img
                src={row.image}
                alt={`Original receipt ${row.fileName}`}
                className="max-h-48 w-auto rounded border border-[var(--border)]"
              />
            ) : null}
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="grid gap-1 text-xs font-medium">
                Supplier
                <Input
                  value={row.vendor}
                  onChange={(event) => update(row.key, { vendor: event.target.value })}
                  placeholder="Supplier name"
                />
              </label>
              <label className="grid gap-1 text-xs font-medium">
                Receipt date
                <Input
                  type="date"
                  value={row.date}
                  onChange={(event) => update(row.key, { date: event.target.value })}
                />
              </label>
              <label className="grid gap-1 text-xs font-medium">
                Total
                <Input
                  inputMode="decimal"
                  value={row.total}
                  onChange={(event) => update(row.key, { total: event.target.value })}
                  placeholder="0.00"
                />
              </label>
              <label className="grid gap-1 text-xs font-medium">
                Reference
                <Input
                  value={row.reference}
                  onChange={(event) => update(row.key, { reference: event.target.value })}
                  placeholder="Order or receipt number"
                />
              </label>
              <label className="grid gap-1 text-xs font-medium sm:col-span-2">
                Expense account
                <select
                  className="input"
                  value={effectiveAccount(row, accounts ?? [])}
                  onChange={(event) => update(row.key, { accountId: event.target.value })}
                >
                  {accounts?.map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.code} · {account.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            {row.hints.length > 0 ? (
              <p className="text-xs text-[var(--muted-foreground)]">{row.hints.join(" ")}</p>
            ) : null}
            {issues.length > 0 ? (
              <p role="alert" className="text-xs text-[var(--danger)]">
                {issues.join(" ")}
              </p>
            ) : null}
          </article>
        );
      })}
      <div className="flex flex-wrap justify-end gap-2">
        <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
          Close
        </Button>
        <Button type="button" onClick={() => void submit()} disabled={blocked}>
          {submitting
            ? "Creating…"
            : `Create draft ${rows.length === 1 ? "bill" : "bills"}`}
        </Button>
      </div>
    </section>
  );
}
